import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { toast } from "react-toastify";
import { constant } from "../../constant";

// Dedicated client so 4xx responses resolve and the server's message (e.g.
// "used by Coventry Homes…") can be shown — the shared apidelete helper masks them.
const client = axios.create({ baseURL: constant.baseUrl, validateStatus: () => true });
const deleteLogo = (id) =>
  client.delete(`logo/${id}`, { headers: { Authorization: localStorage.getItem("token") } });

/**
 * Lists the logo library with where each logo is used, and deletes unused ones
 * (one at a time or several at once). Logos used by an intake form can't be
 * selected — the server refuses those too. Deleting also removes the image
 * from Cloudinary, so it's permanent.
 */
export default function LogoManagerDialog({ open, onClose, logos, onDeleted }) {
  const [selected, setSelected] = useState([]);
  const [confirmIds, setConfirmIds] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!open) setSelected([]);
  }, [open]);

  const unused = useMemo(() => logos.filter((l) => !l.usedBy?.length), [logos]);
  const allUnusedSelected = unused.length > 0 && unused.every((l) => selected.includes(l._id));

  const toggle = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const toggleAllUnused = () => setSelected(allUnusedSelected ? [] : unused.map((l) => l._id));

  const runDelete = async () => {
    const ids = confirmIds || [];
    setDeleting(true);
    const deleted = [];
    // One at a time keeps each Cloudinary delete + DB delete paired and lets
    // us report exactly which ones failed.
    // eslint-disable-next-line no-restricted-syntax
    for (const id of ids) {
      // eslint-disable-next-line no-await-in-loop
      const res = await deleteLogo(id);
      if (res?.status === 200) {
        deleted.push(id);
      } else {
        const name = logos.find((l) => l._id === id)?.name || "Logo";
        toast.error(res?.data?.message || `Couldn't delete "${name}"`);
      }
    }
    setDeleting(false);
    setConfirmIds(null);
    setSelected((prev) => prev.filter((id) => !deleted.includes(id)));
    if (deleted.length) {
      toast.success(deleted.length === 1 ? "Logo deleted" : `${deleted.length} logos deleted`);
      onDeleted(deleted);
    }
  };

  const confirmNames = (confirmIds || []).map((id) => logos.find((l) => l._id === id)?.name).filter(Boolean);

  return (
    <>
      <Dialog open={open} onClose={deleting ? undefined : onClose} fullWidth maxWidth="sm">
        <DialogTitle>Manage Logos</DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            Logos used by an intake form can&apos;t be deleted — switch those forms to a different logo first.
            Deleting a logo also removes its image file permanently.
          </Alert>

          {logos.length === 0 ? (
            <Typography color="text.secondary">No logos in the library yet.</Typography>
          ) : (
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={allUnusedSelected}
                    indeterminate={selected.length > 0 && !allUnusedSelected}
                    onChange={toggleAllUnused}
                    disabled={!unused.length || deleting}
                  />
                }
                label={`Select all unused (${unused.length} of ${logos.length})`}
              />
              <List dense disablePadding>
                {logos.map((logo) => {
                  const inUse = logo.usedBy?.length > 0;
                  return (
                    <ListItem
                      key={logo._id}
                      disableGutters
                      divider
                      secondaryAction={
                        <Tooltip title={inUse ? "In use — can't delete" : "Delete"}>
                          <span>
                            <IconButton
                              edge="end"
                              aria-label={`Delete ${logo.name}`}
                              onClick={() => setConfirmIds([logo._id])}
                              disabled={inUse || deleting}
                            >
                              <DeleteOutlineIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      }
                    >
                      <Checkbox
                        edge="start"
                        checked={selected.includes(logo._id)}
                        onChange={() => toggle(logo._id)}
                        disabled={inUse || deleting}
                        inputProps={{ "aria-label": `Select ${logo.name}` }}
                      />
                      <Box
                        component="img"
                        src={logo.url}
                        alt=""
                        sx={{
                          width: 64,
                          height: 40,
                          objectFit: "contain",
                          mr: 2,
                          flexShrink: 0,
                          borderRadius: 0.5,
                          bgcolor: "grey.200",
                        }}
                      />
                      <ListItemText
                        primary={logo.name}
                        secondary={
                          inUse
                            ? `Used by ${logo.usedBy.map((f) => f.title).join(", ")}`
                            : "Not used by any form"
                        }
                        secondaryTypographyProps={{ color: inUse ? "warning.dark" : "text.secondary" }}
                      />
                    </ListItem>
                  );
                })}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={deleting}>
            Close
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => setConfirmIds(selected)}
            disabled={!selected.length || deleting}
          >
            Delete Selected ({selected.length})
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirmIds} onClose={deleting ? undefined : () => setConfirmIds(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {confirmIds?.length === 1 ? "Delete this logo?" : `Delete ${confirmIds?.length || 0} logos?`}
        </DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            This permanently removes {confirmIds?.length === 1 ? "the image" : "the images"} and can&apos;t be undone.
            <Stack component="ul" sx={{ pl: 2.5, mt: 1, mb: 0, maxHeight: 180, overflowY: "auto" }}>
              {confirmNames.map((n) => (
                <li key={n}>{n}</li>
              ))}
            </Stack>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmIds(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={runDelete} disabled={deleting}>
            {deleting ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

LogoManagerDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  logos: PropTypes.array.isRequired,
  onDeleted: PropTypes.func.isRequired,
};
